<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineAsyncComponent } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '../stores/userStore'
import { useProjectionsStore } from '../stores/projections'
import { useMenuStore } from '../stores/menuStore'
import { useCurrentUserPermissions } from '../composables/usePermissions'
import axios from '../axios.js'
import AllPlayerNewsModal from './AllPlayerNewsModal.vue'
import ExpansionLogDebugPopup from './ExpansionLogDebugPopup.vue'
import ComparePlayersOverlay from './ComparePlayersOverlay.vue'
import LeaguesFixturesOverlay from './LeaguesFixturesOverlay.vue'
import GlobalSettingsPanel from './GlobalSettingsPanel.vue'
import ClientLeaguesManager from './ClientLeaguesManager.vue'
import RealGmLeaguesMappingOverlay from './RealGmLeaguesMappingOverlay.vue'
import TeamDataManagementOverlay from './TeamDataManagementOverlay.vue'
import PBScraperSettingsContent from './PBScraperSettingsContent.vue'
import ManualBoxscoreInsertOverlay from './manual-boxscore-insert/ManualBoxscoreInsertOverlay.vue'
import AgentProcessesOverlay from './AgentProcessesOverlay.vue'
import SlackManagerOverlay from './SlackManagerOverlay.vue'

const ComparePlayerDataV2Overlay = defineAsyncComponent(() => import('./ComparePlayerDataV2Overlay.vue'))
const showCompareV2 = ref(false)
const DataMappingsOverlay = defineAsyncComponent(() => import('./DataMappingsOverlay.vue'))
const showDataMappings = ref(false)
const RealGmSyncOverlay = defineAsyncComponent(() => import('./RealGmSyncOverlay.vue'))
const showRealGmSync = ref(false)
const PlayerCareerHistoryOverlay = defineAsyncComponent(() => import('./PlayerCareerHistoryOverlay.vue'))
const showPlayerCareerHistory = ref(false)
const SportradarMappingsOverlay = defineAsyncComponent(() => import('./SportradarMappingsOverlay.vue'))
const showSportradarMappings = ref(false)
// Prefill the career history search with the fixture id, only on fixture pages.
const careerHistoryQuery = computed(() => (/\/fixture/.test(route.path) ? fixtureId.value : null))

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const projectionsStore = useProjectionsStore()
const menuStore = useMenuStore()
const { hasAnyPermission } = useCurrentUserPermissions()

// Get fixtureId from route if available
const fixtureId = computed(() => {
  // Check various possible route param names
  const id = route.params.id || route.params.fixtureId || route.params.fixture_id || null
  // Also check if route path contains /fixture/ or /fixture-v1/
  if (!id && /\/fixture(?:-v\d+)?\//.test(route.path)) {
    const match = route.path.match(/\/fixture(?:-v\d+)?\/(\d+)/)
    if (match) return match[1]
  }
  return id
})

const showMenu = ref(false)
const menuPosition = ref({ x: 0, y: 0 })
const showAllNewsModal = ref(false)
const showCompareOverlay = ref(false)
const selectedPlayers = ref({})
const activeCompareTab = ref('boxscores')
const showClipboardModal = ref(false)
const clipboardContent = ref('')
const clipboardError = ref('')
const clipboardId = ref('')
const showLeaguesFixturesOverlay = computed({
  get: () => menuStore.leaguesFixturesOverlay,
  set: (value) => {
    menuStore.leaguesFixturesOverlay = value
  }
})
const showGlobalSettings = ref(false)
const showClientLeaguesManager = ref(false)
const showRealGmLeaguesMapping = ref(false)
const showTeamDataManagement = ref(false)
const showPBScraperSettings = ref(false)
const showManualBoxscoreInsert = ref(false)
const showAgentProcesses = ref(false)
const showSlackManager = ref(false)
const showExpansionLogPanel = ref(false)
const marketCellContext = ref(null) // { fixtureId, playerId, market, playerName, marketLabel, rect }
const toolsExpanded = ref(false)
const apiCallsExpanded = ref(false)
const expandedApiCall = ref(null) // Track which API call submenu is expanded
const activeTab = ref('menu')
const pinnedItems = ref([])
const searchQuery = ref('')
const selectedIndex = ref(-1)
const isPlayerSearch = ref(false)
const playerSearchResults = ref([])
const playerSearchLoading = ref(false)
const playerSearchTimeout = ref(null)
const showNotification = ref(false)
const notificationMessage = ref('')
const notificationType = ref('success') // 'success' or 'error'

// Mockup data for API call history
const apiCallHistory = ref({
  'event-created': [
    {
      id: 1,
      timestamp: '2025-01-15 14:32:15',
      status: 200,
      endpoint: '/api/webhooks/fixture.created',
      payloadSize: '2.4 KB',
      responseTime: '145ms'
    },
    {
      id: 2,
      timestamp: '2025-01-15 13:18:42',
      status: 200,
      endpoint: '/api/webhooks/fixture.created',
      payloadSize: '2.1 KB',
      responseTime: '132ms'
    },
    {
      id: 3,
      timestamp: '2025-01-15 12:05:28',
      status: 500,
      endpoint: '/api/webhooks/fixture.created',
      payloadSize: '2.3 KB',
      responseTime: '234ms'
    }
  ],
  'event-updated': [
    {
      id: 1,
      timestamp: '2025-01-15 15:45:22',
      status: 200,
      endpoint: '/api/webhooks/fixture.updated',
      payloadSize: '3.1 KB',
      responseTime: '167ms'
    },
    {
      id: 2,
      timestamp: '2025-01-15 15:30:10',
      status: 200,
      endpoint: '/api/webhooks/fixture.updated',
      payloadSize: '2.9 KB',
      responseTime: '152ms'
    },
    {
      id: 3,
      timestamp: '2025-01-15 15:15:05',
      status: 200,
      endpoint: '/api/webhooks/fixture.updated',
      payloadSize: '3.2 KB',
      responseTime: '189ms'
    }
  ],
  'event-closed': [
    {
      id: 1,
      timestamp: '2025-01-15 16:20:33',
      status: 200,
      endpoint: '/api/webhooks/fixture.closed',
      payloadSize: '1.8 KB',
      responseTime: '98ms'
    },
    {
      id: 2,
      timestamp: '2025-01-15 16:05:18',
      status: 200,
      endpoint: '/api/webhooks/fixture.closed',
      payloadSize: '1.9 KB',
      responseTime: '112ms'
    }
  ],
  'event-settlement': [
    {
      id: 1,
      timestamp: '2025-01-15 17:10:45',
      status: 200,
      endpoint: '/api/webhooks/fixture.settlement',
      payloadSize: '2.7 KB',
      responseTime: '201ms'
    },
    {
      id: 2,
      timestamp: '2025-01-15 16:55:30',
      status: 200,
      endpoint: '/api/webhooks/fixture.settlement',
      payloadSize: '2.5 KB',
      responseTime: '178ms'
    }
  ]
})

// Load pinned items from localStorage
function loadPinnedItems() {
  try {
    const stored = localStorage.getItem('globalContextMenuPinned')
    if (stored) {
      pinnedItems.value = JSON.parse(stored)
    }
  } catch (err) {
    console.error('Failed to load pinned items:', err)
  }
}

// Save pinned items to localStorage
function savePinnedItems() {
  try {
    localStorage.setItem('globalContextMenuPinned', JSON.stringify(pinnedItems.value))
  } catch (err) {
    console.error('Failed to save pinned items:', err)
  }
}

// Check if item is pinned
function isPinned(itemId) {
  return pinnedItems.value.some(item => item.id === itemId)
}

// Toggle pin status
function togglePin(item) {
  const index = pinnedItems.value.findIndex(p => p.id === item.id)
  if (index > -1) {
    pinnedItems.value.splice(index, 1)
  } else {
    // Create a copy of the item for pinning (without group property if it's a tool)
    const pinnedItem = { ...item }
    delete pinnedItem.group
    pinnedItems.value.push(pinnedItem)
  }
  savePinnedItems()
}

const baseMenuItems = [

  {
    id: 'search',
    label: 'Global Search',
    icon: 'search',
    action: () => {
      projectionsStore.globalSearchPopup = true
    }
  },
  {
    id: 'copy-url',
    label: 'Copy Page URL',
    icon: 'copy',
    action: async () => {
      try {
        await navigator.clipboard.writeText(window.location.href)
        console.log('URL copied to clipboard')
      } catch (err) {
        console.error('Failed to copy URL:', err)
      }
    }
  },
  {
    id: 'compare-data',
    label: 'Compare Players Data',
    icon: 'search',
    action: () => {
      activeCompareTab.value = 'boxscores'
      showCompareOverlay.value = true
    }
  },
  {
    id: 'compare-player-data-v2',
    label: 'Compare Player Data v2',
    icon: 'search',
    action: () => { showCompareV2.value = true }
  },
  {
    id: 'data-mappings',
    label: 'Data Mappings',
    icon: 'search',
    action: () => { showDataMappings.value = true }
  },
  {
    id: 'player-career-history',
    label: 'Player career history',
    icon: 'search',
    action: () => { showPlayerCareerHistory.value = true }
  },
  {
    id: 'compare-rosters',
    label: 'Roster',
    icon: 'home',
    action: () => {
      activeCompareTab.value = 'rosters'
      showCompareOverlay.value = true
    }
  },
  {
    id: 'team-data-management',
    label: 'Team Data Management',
    icon: 'cog',
    action: () => {
      showTeamDataManagement.value = true
    }
  },
  {
    id: 'compare-boxscores',
    label: 'Boxscores',
    icon: 'copy',
    action: () => {
      activeCompareTab.value = 'boxscores'
      showCompareOverlay.value = true
    }
  },
  {
    id: 'manual-boxscore-insert',
    label: 'Manual boxscore insert',
    icon: 'copy',
    action: () => {
      showManualBoxscoreInsert.value = true
    }
  },
  {
    id: 'realgm-sync',
    label: 'RealGM Sync',
    icon: 'refresh',
    action: () => { showRealGmSync.value = true }
  },
  {
    id: 'agent-processes',
    label: 'Agent processes',
    icon: 'terminal',
    action: () => {
      showAgentProcesses.value = true
    }
  },
  {
    id: 'divider-1',
    type: 'divider'
  },
  {
    id: 'leagues-fixtures',
    label: 'Leagues → Fixtures',
    icon: 'home',
    action: () => {
      menuStore.leaguesFixturesOverlay = true
    }
  },
  {
    id: 'open-fixture-from-clipboard',
    label: 'Open Fixture based of clipboard ID',
    icon: 'search',
    action: async () => {
      await openFixtureFromClipboard()
    }
  },
  {
    id: 'read-clipboard',
    label: 'Read Clipboard',
    icon: 'copy',
    action: async () => {
      await readClipboard()
    }
  },
  {
    id: 'divider-2',
    type: 'divider'
  },
  {
    id: 'all-news',
    label: 'All News',
    icon: 'news',
    action: () => {
      showAllNewsModal.value = true
    }
  },
  {
    id: 'divider-3',
    type: 'divider'
  },
  {
    id: 'global-settings',
    label: 'Global Settings',
    icon: 'cog',
    action: () => {
      showGlobalSettings.value = true
    }
  },
  {
    id: 'manage-clients-leagues',
    label: 'Manage Clients Leagues',
    icon: 'cog',
    action: () => {
      showClientLeaguesManager.value = true
    }
  },
  {
    id: 'messageboard',
    label: 'Messageboard',
    icon: 'news',
    action: () => {
      router.push('/messageboard')
    }
  },
  {
    id: 'slack-manager',
    label: 'Slack Manager',
    icon: 'bell',
    action: () => {
      showSlackManager.value = true
    }
  },
  {
    id: 'divider-tools',
    type: 'divider'
  },
  {
    id: 'tools-header',
    type: 'header',
    label: 'TOOLS',
    collapsible: true
  },
  {
    id: 'sportradar-mappings',
    label: 'Sportradar Mappings',
    icon: 'search',
    group: 'tools',
    show: () => userStore.currentUser?.role === 'admin',
    action: () => { showSportradarMappings.value = true }
  },
  {
    id: 'scraper-1',
    label: 'Scraper 1',
    icon: 'cog',
    group: 'tools',
    action: () => {
      window.open('https://proballers.tools.oddscomposer.io/', '_blank')
    }
  },
  {
    id: 'scraper-2',
    label: 'Scraper 2',
    icon: 'cog',
    group: 'tools',
    action: () => {
      window.open('https://realgm-scraper.tools.oddscomposer.io/', '_blank')
    }
  },
  {
    id: 'realm-leagues-mapping',
    label: 'RealGM Leagues',
    icon: 'cog',
    group: 'tools',
    action: () => {
      showRealGmLeaguesMapping.value = true
    }
  },
  {
    id: 'odds-monitoring-tool',
    label: 'Odds Monitoring Tool',
    icon: 'search',
    group: 'tools',
    action: () => {
      window.open('https://go-monitoring-tool.tools.oddscomposer.io/arbitrage', '_blank')
    }
  },
  {
    id: 'reset-odds-monitoring-tool',
    label: 'Reset Odds-monitoring-tool',
    icon: 'cog',
    group: 'tools',
    action: async () => {
      await resetMonitoringTool()
    }
  },
  {
    id: 'player-mapping-list',
    label: 'Player Mapping List',
    icon: 'copy',
    group: 'tools',
    action: () => {
      window.open('https://go-monitoring-tool.tools.oddscomposer.io/api/players/list?format=html', '_blank')
    }
  },
  {
    id: 'player-events',
    label: 'Oddsmarket Events',
    icon: 'news',
    group: 'tools',
    action: () => {
      window.open('https://go-monitoring-tool.tools.oddscomposer.io/api/events/list', '_blank')
    }
  },
  {
    id: 'pb-scraper-settings',
    label: 'PB Scraper Settings',
    icon: 'cog',
    group: 'tools',
    action: () => {
      showPBScraperSettings.value = true
    }
  }
]

// Computed menu items that include fixture section when fixtureId is available
const menuItems = computed(() => {
  const fixtureSection = fixtureId.value ? [
    {
      id: 'fixture-header',
      type: 'header',
      label: 'FIXTURE'
    },
    {
      id: 'copy-fixture-url',
      label: `Copy Fixture URL (ID: ${fixtureId.value})`,
      icon: 'copy',
      action: async () => {
        try {
          await navigator.clipboard.writeText(window.location.href)
          console.log('Fixture URL copied to clipboard')
        } catch (err) {
          console.error('Failed to copy fixture URL:', err)
        }
      }
    },
    {
      id: 'copy-fixture-id',
      label: `Copy Fixture ID (${fixtureId.value})`,
      icon: 'copy',
      action: async () => {
        try {
          await navigator.clipboard.writeText(fixtureId.value.toString())
          console.log('Fixture ID copied to clipboard')
        } catch (err) {
          console.error('Failed to copy fixture ID:', err)
        }
      }
    },
    ...(marketCellContext.value
      ? [{
          id: 'open-expansion-log',
          label: 'Open Expansion Log',
          icon: 'search',
          action: () => {
            showExpansionLogPanel.value = true
            closeMenu()
          }
        }]
      : []),
    {
      id: 'api-calls-header',
      type: 'header',
      label: 'API CALLS',
      collapsible: true
    },
    {
      id: 'event-created',
      label: 'API.event.created',
      icon: 'cog',
      group: 'api-calls',
      hasSubmenu: true,
      historyCount: () => apiCallHistory.value['event-created']?.length || 0,
      action: async () => {
        console.log(`API.event.created for fixture ${fixtureId.value}`)
        // TODO: Implement API call
      }
    },
    {
      id: 'event-updated',
      label: 'API.event.updated',
      icon: 'cog',
      group: 'api-calls',
      hasSubmenu: true,
      historyCount: () => apiCallHistory.value['event-updated']?.length || 0,
      action: async () => {
        console.log(`API.event.updated for fixture ${fixtureId.value}`)
        // TODO: Implement API call
      }
    },
    {
      id: 'event-closed',
      label: 'API.event.closed',
      icon: 'cog',
      group: 'api-calls',
      hasSubmenu: true,
      historyCount: () => apiCallHistory.value['event-closed']?.length || 0,
      action: async () => {
        console.log(`API.event.closed for fixture ${fixtureId.value}`)
        // TODO: Implement API call
      }
    },
    {
      id: 'event-settlement',
      label: 'API.event.settlement',
      icon: 'cog',
      group: 'api-calls',
      hasSubmenu: true,
      historyCount: () => apiCallHistory.value['event-settlement']?.length || 0,
      action: async () => {
        console.log(`API.event.settlement for fixture ${fixtureId.value}`)
        // TODO: Implement API call
      }
    },
    {
      id: 'api-statistics',
      label: 'API Statistics',
      icon: 'cog',
      group: 'api-calls',
      action: () => {
        console.log(`API Statistics for fixture ${fixtureId.value}`)
        // TODO: Implement API Statistics
      }
    },
    {
      id: 'divider-fixture',
      type: 'divider'
    }
  ] : []
  
  return [...fixtureSection, ...baseMenuItems]
})

const iconPaths = {
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  news: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
  cog: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  terminal: 'M8 9l3 3-3 3m5 0h3M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
  bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  chevronDown: 'M19 9l-7 7-7-7',
  chevronRight: 'M9 5l7 7-7 7',
  pin: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z',
  pinSolid: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z',
}

async function handleContextMenu(event) {
  // Don't show global menu if clicking on elements that have their own context menu
  const target = event.target
  if (target.closest('[data-no-global-context-menu]')) {
    return
  }
  
  event.preventDefault()
  event.stopPropagation()
  
  // Detect market cell context (balanced lines)
  const cell = target.closest('[data-market-cell]')
  if (cell && hasAnyPermission.value?.([ 'debug.view', 'markets.debug', 'admin.access' ])) {
    const rect = cell.getBoundingClientRect()
    marketCellContext.value = {
      fixtureId: cell.dataset.fixtureId || null,
      playerId: cell.dataset.playerId || null,
      market: cell.dataset.market || '',
      playerName: cell.dataset.playerName || '',
      marketLabel: cell.dataset.marketLabel || '',
      rect: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width }
    }
  } else {
    marketCellContext.value = null
  }
  
  // Read clipboard to get ID
  await readClipboardId()
  
  menuPosition.value = {
    x: event.clientX,
    y: event.clientY
  }
  showMenu.value = true
  // Initialize selected index to first item when menu opens
  // Use nextTick to ensure menu is rendered
  setTimeout(() => {
    const items = getVisibleMenuItems().filter(item => item.type !== 'divider' && item.type !== 'header')
    selectedIndex.value = items.length > 0 ? 0 : -1
  }, 0)
}

function closeMenu() {
  showMenu.value = false
  toolsExpanded.value = false
  apiCallsExpanded.value = false
  expandedApiCall.value = null
  activeTab.value = 'menu'
  searchQuery.value = ''
  selectedIndex.value = -1
  isPlayerSearch.value = false
  playerSearchResults.value = []
  if (!showExpansionLogPanel.value) marketCellContext.value = null
  
  // Clear player search timeout
  if (playerSearchTimeout.value) {
    clearTimeout(playerSearchTimeout.value)
    playerSearchTimeout.value = null
  }
}

function handleMenuClick(item) {
  // Don't close menu if item has submenu - let toggleApiCallSubmenu handle it
  if (item.hasSubmenu) {
    toggleApiCallSubmenu(item.id)
    return
  }
  
  if (item.action) {
    item.action()
  }
  closeMenu()
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(() => {
    console.log('Copied to clipboard')
    // You could add a toast notification here
  }).catch(err => {
    console.error('Failed to copy:', err)
  })
}

function handleClickOutside(event) {
  if (showExpansionLogPanel.value) {
    const panelElement = document.querySelector('.expansion-log-panel')
    if (panelElement && !panelElement.contains(event.target)) {
      showExpansionLogPanel.value = false
      marketCellContext.value = null
    }
    return
  }
  if (!showMenu.value) return
  const menuElement = document.querySelector('.global-context-menu')
  if (menuElement && !menuElement.contains(event.target)) {
    closeMenu()
  }
}

function handleMouseDown(event) {
  if (showExpansionLogPanel.value) {
    const panelElement = document.querySelector('.expansion-log-panel')
    if (panelElement && !panelElement.contains(event.target)) {
      showExpansionLogPanel.value = false
      marketCellContext.value = null
    }
    return
  }
  if (!showMenu.value) return
  const menuElement = document.querySelector('.global-context-menu')
  if (menuElement && !menuElement.contains(event.target)) {
    closeMenu()
  }
}

function handleEscape(event) {
  if (event.key === 'Escape') {
    if (showExpansionLogPanel.value) {
      showExpansionLogPanel.value = false
      marketCellContext.value = null
      return
    }
    if (!showMenu.value) return
    closeMenu()
  } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    // Handle arrow keys when menu is open
    event.preventDefault()
    
    if (isPlayerSearch.value) {
      // Handle player search results navigation
      if (playerSearchResults.value.length === 0) return
      
      if (selectedIndex.value < 0) {
        selectedIndex.value = 0
      }
      
      if (event.key === 'ArrowDown') {
        selectedIndex.value = (selectedIndex.value + 1) % playerSearchResults.value.length
      } else {
        selectedIndex.value = selectedIndex.value <= 0 ? playerSearchResults.value.length - 1 : selectedIndex.value - 1
      }
    } else {
      // Handle regular menu items navigation
      const items = getVisibleMenuItems().filter(item => item.type !== 'divider' && item.type !== 'header')
      
      if (items.length === 0) return
      
      // Initialize selectedIndex if not set
      if (selectedIndex.value < 0) {
        selectedIndex.value = 0
      }
      
      if (event.key === 'ArrowDown') {
        selectedIndex.value = (selectedIndex.value + 1) % items.length
      } else {
        selectedIndex.value = selectedIndex.value <= 0 ? items.length - 1 : selectedIndex.value - 1
      }
      
      // Scroll selected item into view
      setTimeout(() => {
        const selectedElement = document.querySelector(`[data-menu-index="${selectedIndex.value}"]`)
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      }, 0)
    }
  } else if (event.key === 'Enter') {
    event.preventDefault()
    
    if (isPlayerSearch.value) {
      // Handle player selection
      if (playerSearchResults.value.length > 0 && selectedIndex.value >= 0) {
        const selectedPlayer = playerSearchResults.value[selectedIndex.value]
        router.push(`/player/${selectedPlayer.player_id || selectedPlayer.id}`)
        closeMenu()
      }
    } else {
      // Handle regular menu item selection
      const items = getVisibleMenuItems().filter(item => item.type !== 'divider' && item.type !== 'header')
      
      if (items.length > 0) {
        // Use selectedIndex if set, otherwise use first item
        const indexToUse = selectedIndex.value >= 0 ? selectedIndex.value : 0
        if (indexToUse < items.length) {
          const selectedItem = items[indexToUse]
          if (selectedItem && selectedItem.action) {
            selectedItem.action()
            closeMenu()
          }
        }
      }
    }
  }
}

const handleOpenCompareOverlay = (event) => {
  const tab = event?.detail?.tab
  if (tab) {
    activeCompareTab.value = tab
  }
  showCompareOverlay.value = true
}

// Get menu index for an item (excluding dividers and headers)
function getMenuIndex(items, currentIndex) {
  let menuIndex = -1
  for (let i = 0; i <= currentIndex; i++) {
    if (items[i] && items[i].type !== 'divider' && items[i].type !== 'header') {
      menuIndex++
    }
  }
  return menuIndex
}

// Search players function
const searchPlayers = async (query) => {
  playerSearchLoading.value = true
  try {
    const params = { 
      search: query || ''
    }
    
    console.log('🔍 Searching players with query:', query, 'params:', params)
    
    const response = await axios.get('/api/players/global-search', { params })
    console.log('✅ Player search response:', response.data)
    playerSearchResults.value = response.data || []
  } catch (error) {
    console.error('❌ Error searching players:', error)
    playerSearchResults.value = []
  } finally {
    playerSearchLoading.value = false
  }
}

// Debounced player search
const debouncedPlayerSearch = (query) => {
  // Clear existing timeout
  if (playerSearchTimeout.value) {
    clearTimeout(playerSearchTimeout.value)
  }
  
  // Extract search term after "Player:"
  const playerPrefix = 'Player:'
  const trimmedQuery = query.trim()
  const playerSearchTerm = trimmedQuery.substring(playerPrefix.length).trim()
  
  console.log('⏱️ Debouncing player search, term:', playerSearchTerm)
  
  // Set timeout for debounced search
  playerSearchTimeout.value = setTimeout(() => {
    searchPlayers(playerSearchTerm)
  }, 300) // 300ms delay
}

// Extract fixture ID from search: plain digits or URL like .../fixture/3124/projections
const fixtureIdFromSearch = computed(() => {
  const q = searchQuery.value.trim()
  if (!q) return null
  if (isPlayerSearch.value) return null
  const onlyDigits = q.match(/^\d+$/)
  if (onlyDigits) return onlyDigits[0]
  const urlMatch = q.match(/fixture(?:-v\d+)?\/(\d+)/i)
  if (urlMatch) return urlMatch[1]
  return null
})

// Watch search query to reset selected index and handle player search
watch(searchQuery, (newVal, oldVal) => {
  console.log('🔤 Search query changed:', newVal, 'old:', oldVal)
  
  // Check if query starts with "Player:" prefix
  const playerPrefix = 'Player:'
  const isPlayerQuery = newVal && newVal.trim().toLowerCase().startsWith(playerPrefix.toLowerCase())
  
  if (isPlayerQuery) {
    console.log('👤 Player query detected!')
    isPlayerSearch.value = true
    playerSearchResults.value = []
    debouncedPlayerSearch(newVal)
    selectedIndex.value = 0
  } else {
    isPlayerSearch.value = false
    playerSearchResults.value = []
    
    if (newVal.trim()) {
      // Reset to first item when searching menu items
      selectedIndex.value = 0
    } else if (showMenu.value) {
      // When search is cleared but menu is still open, reset to first item
      const items = getVisibleMenuItems().filter(item => item.type !== 'divider' && item.type !== 'header')
      selectedIndex.value = items.length > 0 ? 0 : -1
    }
  }
})

onMounted(() => {
  loadPinnedItems()
  document.addEventListener('contextmenu', handleContextMenu)
  // Use capture phase to catch events before they bubble
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('mousedown', handleMouseDown, true)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('open-compare-overlay', handleOpenCompareOverlay)
  window.addEventListener('open-expansion-log', handleOpenExpansionLog)
})

function handleOpenExpansionLog(event) {
  const { fixtureId, playerId, market, playerName, marketLabel, rect } = event.detail || {}
  if (!rect) return
  marketCellContext.value = { fixtureId, playerId, market, playerName, marketLabel, rect }
  showExpansionLogPanel.value = true
}

onBeforeUnmount(() => {
  document.removeEventListener('contextmenu', handleContextMenu)
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('mousedown', handleMouseDown, true)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('open-compare-overlay', handleOpenCompareOverlay)
  window.removeEventListener('open-expansion-log', handleOpenExpansionLog)
})

function getVisibleMenuItems() {
  const isSearching = searchQuery.value.trim().length > 0
  
  // Get items based on active tab
  let items = []
  if (activeTab.value === 'pinned') {
    items = pinnedItems.value
  } else {
    // When searching, show all items regardless of expansion state
    if (isSearching) {
      items = menuItems.value.filter(item => {
        if (item.type === 'divider' || item.type === 'header') return true
        if (item.show && typeof item.show === 'function') {
          return item.show()
        }
        return true
      })
    } else {
      // Normal mode - respect expansion state
      items = menuItems.value.filter(item => {
        if (item.type === 'divider' || item.type === 'header') return true
        // Hide tool items when tools section is collapsed
        if (item.group === 'tools' && !toolsExpanded.value) return false
        // Hide api-calls items when api-calls section is collapsed
        if (item.group === 'api-calls' && !apiCallsExpanded.value) return false
        if (item.show && typeof item.show === 'function') {
          return item.show()
        }
        return true
      })
    }
  }
  
  // Filter by search query if provided
  if (isSearching) {
    const query = searchQuery.value.toLowerCase().trim()
    const filtered = []

    // When search is a fixture ID (digits only or URL containing /fixture/123/), prepend "Go to Fixture"
    const fid = fixtureIdFromSearch.value
    if (fid && activeTab.value === 'menu') {
      filtered.push({
        id: 'go-to-fixture',
        label: `Go to Fixture ${fid} →`,
        icon: 'home',
        action: () => {
          router.push(`/fixture/${fid}/projections`)
          closeMenu()
        }
      })
    }
    
    items.forEach((item, index) => {
      if (item.type === 'divider' || item.type === 'header') {
        // For dividers and headers, check if there are matching items nearby
        const hasMatchingBefore = index > 0 && items[index - 1]?.label?.toLowerCase().includes(query)
        const hasMatchingAfter = index < items.length - 1 && items[index + 1]?.label?.toLowerCase().includes(query)
        
        // Include header if it has matching items after it
        if (item.type === 'header' && hasMatchingAfter) {
          filtered.push(item)
        }
        // Include divider if there are matching items on either side
        else if (item.type === 'divider' && (hasMatchingBefore || hasMatchingAfter)) {
          // Only add if last item wasn't already a divider
          if (filtered.length === 0 || filtered[filtered.length - 1].type !== 'divider') {
            filtered.push(item)
          }
        }
      } else {
        // Regular menu item - check if label matches
        if (item.label?.toLowerCase().includes(query)) {
          // Add divider before if previous item was a divider and we don't already have one
          if (index > 0 && items[index - 1]?.type === 'divider') {
            if (filtered.length === 0 || filtered[filtered.length - 1].type !== 'divider') {
              filtered.push(items[index - 1])
            }
          }
          filtered.push(item)
        }
      }
    })
    
    return filtered
  }
  
  return items
}

function toggleTools() {
  toolsExpanded.value = !toolsExpanded.value
}

function toggleApiCalls() {
  apiCallsExpanded.value = !apiCallsExpanded.value
  // Close any expanded submenu when collapsing the main section
  if (!apiCallsExpanded.value) {
    expandedApiCall.value = null
  }
}

function toggleApiCallSubmenu(itemId) {
  if (expandedApiCall.value === itemId) {
    expandedApiCall.value = null
  } else {
    expandedApiCall.value = itemId
  }
}

async function resetMonitoringTool() {
  try {
    const response = await axios.post('/api/monitoring-tool/reset', {
      mysql_host: 'localhost',
      mysql_port: 3306,
      mysql_user: 'root',
      mysql_password: 'CHRI5tma',
      mysql_database: 'api.v2.test.oddscomposer.io.14.08.2025'
    })
    
    if (response.data.success) {
      notificationType.value = 'success'
      notificationMessage.value = response.data.message || 'Monitoring tool reset successfully!'
      showNotification.value = true
      setTimeout(() => {
        showNotification.value = false
      }, 5000)
    } else {
      notificationType.value = 'error'
      notificationMessage.value = response.data.message || 'Failed to reset monitoring tool'
      showNotification.value = true
      setTimeout(() => {
        showNotification.value = false
      }, 5000)
    }
  } catch (error) {
    console.error('Error resetting monitoring tool:', error)
    notificationType.value = 'error'
    notificationMessage.value = error.response?.data?.message || error.message || 'Failed to reset monitoring tool'
    showNotification.value = true
    setTimeout(() => {
      showNotification.value = false
    }, 5000)
  }
  closeMenu()
}

async function readClipboard() {
  clipboardError.value = ''
  clipboardContent.value = ''
  
  try {
    // Check if clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      clipboardError.value = 'Clipboard API is not available. Make sure you are using a secure context (HTTPS).'
      showClipboardModal.value = true
      return
    }
    
    // Request permission and read clipboard
    const text = await navigator.clipboard.readText()
    clipboardContent.value = text || '(empty)'
    showClipboardModal.value = true
  } catch (err) {
    clipboardError.value = 'Failed to read clipboard: ' + (err.message || 'Permission denied. Make sure clipboard permission is granted.')
    clipboardContent.value = ''
    showClipboardModal.value = true
    console.error('Failed to read clipboard:', err)
  }
}

async function readClipboardId() {
  clipboardId.value = ''
  
  try {
    // Check if clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      return
    }
    
    // Read clipboard
    const clipboardText = await navigator.clipboard.readText()
    
    if (!clipboardText || !clipboardText.trim()) {
      return
    }
    
    // Extract ID (handle numbers, may have whitespace)
    const id = clipboardText.trim().match(/^\d+$/)?.[0]
    
    if (id) {
      clipboardId.value = id
    }
  } catch (err) {
    // Silently fail - clipboard might not be accessible
    console.error('Failed to read clipboard ID:', err)
  }
}

async function openFixtureFromClipboard() {
  if (!clipboardId.value) {
    // Try reading again in case clipboard changed
    await readClipboardId()
  }
  
  if (!clipboardId.value) {
    alert('No valid fixture ID found in clipboard.')
    return
  }
  
  // Navigate to fixture page
  router.push(`/fixture/${clipboardId.value}`)
}

function closeClipboardModal() {
  showClipboardModal.value = false
  clipboardContent.value = ''
  clipboardError.value = ''
}

function copyClipboardContent() {
  if (!clipboardContent.value || clipboardContent.value === '(empty)') return
  
  navigator.clipboard.writeText(clipboardContent.value).then(() => {
    console.log('Content copied to clipboard')
  }).catch(err => {
    console.error('Failed to copy:', err)
  })
}
</script>

<template>
  <div 
    v-if="showMenu"
    class="global-context-menu fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] w-[360px]"
    :style="{ 
      left: menuPosition.x + 'px', 
      top: menuPosition.y + 'px',
      maxHeight: '80vh',
      overflowY: 'auto'
    }"
    @click.stop
  >
    <!-- Tabs -->
    <div class="flex border-b border-gray-200">
      <button
        @click.stop="activeTab = 'menu'"
        class="flex-1 px-4 py-2 text-sm font-medium transition-colors"
        :class="activeTab === 'menu' 
          ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50' 
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'"
      >
        Menu
      </button>
      <button
        @click.stop="activeTab = 'pinned'"
        class="flex-1 px-4 py-2 text-sm font-medium transition-colors"
        :class="activeTab === 'pinned' 
          ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50' 
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'"
      >
        Pinned
      </button>
    </div>
    
    <!-- Search Input -->
    <div class="px-3 py-2 border-b border-gray-200">
      <div class="relative">
        <svg 
          class="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths.search" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search menu or enter fixture ID..."
          class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          @click.stop
        />
        <button
          v-if="searchQuery"
          @click.stop="searchQuery = ''"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Menu Content -->
    <div class="py-2">
      <!-- Player Search Results -->
      <template v-if="isPlayerSearch">
        <div v-if="playerSearchLoading" class="px-4 py-8 text-center text-gray-500 text-sm">
          <p>Searching players...</p>
        </div>
        <template v-else-if="playerSearchResults.length === 0">
          <div class="px-4 py-8 text-center text-gray-500 text-sm">
            <p>No players found</p>
            <p class="text-xs mt-1">Try a different search term</p>
          </div>
        </template>
        <template v-else>
          <div
            v-for="(player, index) in playerSearchResults"
            :key="player.player_id || player.id"
            class="w-full flex items-center group"
            :class="{ 'bg-pink-50': index === selectedIndex }"
          >
            <button
              @click="router.push(`/player/${player.player_id || player.id}`); closeMenu()"
              class="flex-1 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between gap-3 transition-colors w-full"
              :class="{ 'bg-pink-100 hover:bg-pink-100': index === selectedIndex }"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths.search" />
                </svg>
                <div class="flex flex-col min-w-0">
                  <span class="truncate font-medium">{{ player.player_name || 'Unknown Player' }}</span>
                  <span class="text-xs text-gray-500">ID: {{ player.player_id || player.id }}</span>
                </div>
              </div>
            </button>
          </div>
        </template>
      </template>
      <!-- Regular Menu Items -->
      <template v-else-if="activeTab === 'pinned' && pinnedItems.length === 0 && !searchQuery.trim()">
        <div class="px-4 py-8 text-center text-gray-500 text-sm">
          <p>No pinned items yet</p>
          <p class="text-xs mt-1">Pin items from the Menu tab to access them quickly</p>
        </div>
      </template>
      <template v-else-if="getVisibleMenuItems().length === 0 && searchQuery.trim()">
        <div class="px-4 py-8 text-center text-gray-500 text-sm">
          <p>No results found</p>
          <p class="text-xs mt-1">Try a different search term</p>
        </div>
      </template>
      <template v-else>
        <template v-for="(item, index) in getVisibleMenuItems()" :key="item.id || index">
          <div v-if="item.type === 'divider'" class="border-t border-gray-200 my-1"></div>
          <button
            v-else-if="item.type === 'header' && item.collapsible"
            @click.stop="item.id === 'api-calls-header' ? toggleApiCalls() : toggleTools()"
            class="w-full text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 flex items-center justify-between transition-colors"
          >
            <span>{{ item.label }}</span>
            <svg 
              class="w-3 h-3 transition-transform duration-200" 
              :class="{ 'rotate-90': item.id === 'api-calls-header' ? apiCallsExpanded : toolsExpanded }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths.chevronRight" />
            </svg>
          </button>
          <div
            v-else-if="item.type === 'header'"
            class="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
          >
            {{ item.label }}
          </div>
          <div
            v-else
            class="w-full"
            :data-menu-index="getMenuIndex(getVisibleMenuItems(), index)"
          >
            <!-- API Call Item with Submenu -->
            <div v-if="item.hasSubmenu" class="w-full">
              <div class="flex items-center group">
                <button
                  @click.stop="toggleApiCallSubmenu(item.id)"
                  class="flex-1 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between gap-3 transition-colors"
                  :class="{ 
                    'bg-pink-100 hover:bg-pink-100': getMenuIndex(getVisibleMenuItems(), index) === selectedIndex
                  }"
                >
                  <div class="flex items-center gap-3 flex-1 min-w-0">
                    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths[item.icon] || iconPaths.search" />
                    </svg>
                    <span class="truncate">{{ item.label }}</span>
                    <span v-if="item.historyCount" class="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                      {{ item.historyCount() }}
                    </span>
                  </div>
                  <svg 
                    class="w-3 h-3 transition-transform duration-200 flex-shrink-0" 
                    :class="{ 'rotate-90': expandedApiCall === item.id }"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths.chevronRight" />
                  </svg>
                </button>
                <button
                  @click.stop="togglePin(item)"
                  class="px-2 py-2 transition-opacity hover:bg-gray-100 flex-shrink-0"
                  :class="activeTab === 'menu' 
                    ? (isPinned(item.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')
                    : 'opacity-100'"
                  :title="isPinned(item.id) ? 'Unpin' : 'Pin'"
                >
                  <svg 
                    class="w-4 h-4" 
                    :class="isPinned(item.id) ? 'text-pink-600' : 'text-gray-400'"
                    :fill="isPinned(item.id) ? 'currentColor' : 'none'"
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      stroke-linecap="round" 
                      stroke-linejoin="round" 
                      stroke-width="2" 
                      :d="iconPaths.pin" 
                    />
                  </svg>
                </button>
              </div>
              
              <!-- Submenu with History -->
              <div v-if="expandedApiCall === item.id && apiCallHistory[item.id]" class="bg-gray-50 border-l-2 border-pink-300 ml-4">
                <div class="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                  Recent Calls ({{ apiCallHistory[item.id].length }})
                </div>
                <div
                  v-for="(historyItem, historyIndex) in apiCallHistory[item.id]"
                  :key="historyItem.id"
                  class="px-4 py-2 text-xs hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  @click.stop="copyToClipboard(JSON.stringify(historyItem, null, 2))"
                >
                  <div class="flex items-center justify-between mb-1">
                    <span class="font-medium text-gray-700">{{ historyItem.timestamp }}</span>
                    <span 
                      class="px-1.5 py-0.5 rounded text-xs font-medium"
                      :class="historyItem.status === 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                    >
                      {{ historyItem.status }}
                    </span>
                  </div>
                  <div class="text-gray-600 space-y-0.5">
                    <div class="flex items-center gap-2">
                      <span class="text-gray-500">Endpoint:</span>
                      <span class="truncate">{{ historyItem.endpoint }}</span>
                    </div>
                    <div class="flex items-center gap-4 text-gray-500">
                      <span>Size: {{ historyItem.payloadSize }}</span>
                      <span>Time: {{ historyItem.responseTime }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="apiCallHistory[item.id].length === 0" class="px-4 py-4 text-center text-gray-400 text-xs">
                  No history available
                </div>
              </div>
            </div>
            
            <!-- Regular Menu Item -->
            <div v-else class="flex items-center group">
              <button
                @click="handleMenuClick(item)"
                class="flex-1 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between gap-3 transition-colors"
                :class="{ 
                  'opacity-50 cursor-not-allowed': item.id === 'open-fixture-from-clipboard' && !clipboardId,
                  'bg-pink-100 hover:bg-pink-100': getMenuIndex(getVisibleMenuItems(), index) === selectedIndex
                }"
                :disabled="item.id === 'open-fixture-from-clipboard' && !clipboardId"
              >
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPaths[item.icon] || iconPaths.search" />
                  </svg>
                  <span class="truncate">{{ item.label }}</span>
                </div>
                <span 
                  v-if="item.id === 'open-fixture-from-clipboard' && clipboardId" 
                  class="text-xs font-semibold text-pink-600 bg-pink-50 px-2 py-0.5 rounded flex-shrink-0"
                >
                  ID: {{ clipboardId }}
                </span>
                <span 
                  v-else-if="item.id === 'open-fixture-from-clipboard' && !clipboardId" 
                  class="text-xs text-gray-400 flex-shrink-0"
                >
                  No ID
                </span>
              </button>
              <button
                @click.stop="togglePin(item)"
                class="px-2 py-2 transition-opacity hover:bg-gray-100 flex-shrink-0"
                :class="activeTab === 'menu' 
                  ? (isPinned(item.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')
                  : 'opacity-100'"
                :title="isPinned(item.id) ? 'Unpin' : 'Pin'"
              >
                <svg 
                  class="w-4 h-4" 
                  :class="isPinned(item.id) ? 'text-pink-600' : 'text-gray-400'"
                  :fill="isPinned(item.id) ? 'currentColor' : 'none'"
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    :d="iconPaths.pin" 
                  />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
  
  <!-- All News Modal -->
  <AllPlayerNewsModal 
    :show="showAllNewsModal"
    @close="showAllNewsModal = false"
  />

  <ComparePlayerDataV2Overlay v-if="showCompareV2" @close="showCompareV2 = false" />
  <DataMappingsOverlay v-if="showDataMappings" @close="showDataMappings = false" />
  <SportradarMappingsOverlay v-if="showSportradarMappings" @close="showSportradarMappings = false" />
  <RealGmSyncOverlay v-if="showRealGmSync" @close="showRealGmSync = false" />
  <PlayerCareerHistoryOverlay v-if="showPlayerCareerHistory" :initial-query="careerHistoryQuery" @close="showPlayerCareerHistory = false" />

  <!-- Compare Players Overlay -->
  <ComparePlayersOverlay
    v-model="showCompareOverlay"
    :selected-players="selectedPlayers"
    :initial-tab="activeCompareTab"
    @players-updated="selectedPlayers = $event"
    @close="showCompareOverlay = false"
  />

  <!-- Leagues & Fixtures Overlay -->
  <LeaguesFixturesOverlay
    v-model="showLeaguesFixturesOverlay"
    @close="showLeaguesFixturesOverlay = false"
  />

  <!-- Global Settings Panel -->
  <GlobalSettingsPanel
    :show="showGlobalSettings"
    @update:show="showGlobalSettings = $event"
    @close="showGlobalSettings = false"
  />

  <!-- Client Leagues Manager -->
  <ClientLeaguesManager
    :show="showClientLeaguesManager"
    @update:show="showClientLeaguesManager = $event"
    @close="showClientLeaguesManager = false"
  />

  <!-- RealGM Leagues Mapping Overlay -->
  <RealGmLeaguesMappingOverlay
    :show="showRealGmLeaguesMapping"
    @update:show="showRealGmLeaguesMapping = $event"
    @close="showRealGmLeaguesMapping = false"
  />

  <!-- Team Data Management Overlay -->
  <TeamDataManagementOverlay
    :show="showTeamDataManagement"
    @update:show="showTeamDataManagement = $event"
    @close="showTeamDataManagement = false"
  />

  <!-- PB Scraper Settings Modal -->
  <div 
    v-if="showPBScraperSettings"
    class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-2"
    @click="showPBScraperSettings = false"
  >
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-[95vw] h-[95vh] flex flex-col" @click.stop>
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">PB Scraper Settings</h3>
          <p class="text-xs text-gray-500 mt-1">Configure Proballers schedule fetch command</p>
        </div>
        <button 
          class="text-gray-500 hover:text-gray-700 p-2"
          @click="showPBScraperSettings = false"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="flex-1 overflow-hidden px-6 py-4 min-h-0">
        <PBScraperSettingsContent @close="showPBScraperSettings = false" />
      </div>
    </div>
  </div>

  <!-- Clipboard Modal -->
  <div 
    v-if="showClipboardModal"
    class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
    @click="closeClipboardModal"
  >
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" @click.stop>
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">Clipboard Content</h3>
          <p class="text-xs text-gray-500 mt-1">Current clipboard value</p>
        </div>
        <button 
          class="text-gray-500 hover:text-gray-700 p-2"
          @click="closeClipboardModal"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="flex-1 overflow-auto px-6 py-4">
        <div v-if="clipboardError" class="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <div class="flex items-start gap-2">
            <i class="fas fa-exclamation-circle text-red-600 mt-0.5"></i>
            <div class="text-sm text-red-700">{{ clipboardError }}</div>
          </div>
        </div>
        
        <div v-if="!clipboardError && clipboardContent" class="space-y-3">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700">Content:</label>
            <button
              v-if="clipboardContent && clipboardContent !== '(empty)'"
              @click="copyClipboardContent"
              class="text-xs px-3 py-1 bg-pink-500 text-white rounded hover:bg-pink-600"
              title="Copy to clipboard"
            >
              <i class="fas fa-copy mr-1"></i> Copy
            </button>
          </div>
          <div class="bg-gray-50 border border-gray-200 rounded p-4">
            <pre class="text-sm text-gray-800 whitespace-pre-wrap break-words max-h-[50vh] overflow-auto">{{ clipboardContent }}</pre>
          </div>
          <div class="text-xs text-gray-500">
            Length: {{ clipboardContent === '(empty)' ? 0 : clipboardContent.length }} characters
          </div>
        </div>
        
        <div v-if="!clipboardError && !clipboardContent" class="text-center py-8 text-gray-500">
          <i class="fas fa-clipboard text-4xl mb-2"></i>
          <p>No content to display</p>
        </div>
      </div>
      
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
        <button
          class="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          @click="closeClipboardModal"
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Notification Popup -->
  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="showNotification"
      :class="[
        'fixed bottom-4 right-4 z-[10000] max-w-md w-full shadow-lg rounded-lg p-4 flex items-start gap-3',
        notificationType === 'success' 
          ? 'bg-green-50 border border-green-200' 
          : 'bg-red-50 border border-red-200'
      ]"
    >
      <div class="flex-shrink-0">
        <svg
          v-if="notificationType === 'success'"
          class="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <svg
          v-else
          class="h-6 w-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div class="flex-1">
        <p
          :class="[
            'text-sm font-medium',
            notificationType === 'success' ? 'text-green-800' : 'text-red-800'
          ]"
        >
          {{ notificationMessage }}
        </p>
      </div>
      <button
        @click="showNotification = false"
        :class="[
          'flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none',
          notificationType === 'success' ? 'hover:text-green-600' : 'hover:text-red-600'
        ]"
      >
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </transition>

  <!-- Expansion Log panel (below market cell, no overlay) -->
  <ExpansionLogDebugPopup
    :is-open="showExpansionLogPanel"
    :fixture-id="marketCellContext?.fixtureId ?? null"
    :player-id="marketCellContext?.playerId ?? null"
    :market="marketCellContext?.market ?? ''"
    :player-name="marketCellContext?.playerName ?? ''"
    :market-label="marketCellContext?.marketLabel ?? ''"
    :cell-rect="showExpansionLogPanel && marketCellContext?.rect ? marketCellContext.rect : null"
    @close="showExpansionLogPanel = false; marketCellContext = null"
  />

  <!-- Manual Boxscore Insert Overlay -->
  <ManualBoxscoreInsertOverlay
    :is-visible="showManualBoxscoreInsert"
    @close="showManualBoxscoreInsert = false"
  />

  <!-- Agent Processes Overlay -->
  <AgentProcessesOverlay
    :is-visible="showAgentProcesses"
    @close="showAgentProcesses = false"
  />

  <!-- Slack Manager (alert rules → Slack channels, imported from vmprod saas-monitoring) -->
  <SlackManagerOverlay
    :is-visible="showSlackManager"
    @close="showSlackManager = false"
  />
</template>

<style scoped>
.global-context-menu {
  animation: fadeIn 0.1s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>

